import {
    MiddlewarePersistWatchlistDto,
    MiddlewareWatchlistEntryDto,
    MiddlewareWatchlistEntryDtoTypeEnum,
} from '@orbis-systems/orbis-api-clients/middleware';
import { sortBy, uniqBy } from 'lodash-es';
import { inject, injectable } from 'tsyringe';
import { Optional } from 'utility-types';
import { BaseApi } from '../BaseApi';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import { type Apis, type MiddlewareConfig, Quote, TiprankOverviewWithExtraInfo } from '../types';
import { WatchlistWithQuotes } from '../types/Watchlist.type';
import type { QuoteApi } from './QuoteApi';
import type { TipranksApi } from './TipranksApi';

@injectable()
export class WatchlistApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.QuoteApi) protected readonly quoteApi: QuoteApi,
        @inject(ApiInjectToken.TipranksApi) protected readonly tipranksApi: TipranksApi,
    ) {
        super(apis, config);
    }

    private formatEntries(entries: MiddlewareWatchlistEntryDto[]) {
        return entries.map((entry) => ({
            // we don't want to attach `quote` from `WatchlistEntryWithQuote`
            symbol: entry.symbol,
            type: entry.type,
        }));
    }

    private async getQuotes(entries: MiddlewareWatchlistEntryDto[]): Promise<Quote[]> {
        const uniqueEquityEntries = uniqBy(
            entries.filter(
                (entry) =>
                    entry.type === MiddlewareWatchlistEntryDtoTypeEnum.Equity ||
                    entry.type === MiddlewareWatchlistEntryDtoTypeEnum.MutualFund,
            ),
            (entry) => entry.symbol,
        );
        const uniqueCryptoEntries = uniqBy(
            entries.filter((entry) => entry.type === MiddlewareWatchlistEntryDtoTypeEnum.Crypto),
            (entry) => entry.symbol,
        );
        const uniqueOptionEntries = uniqBy(
            entries.filter((entry) => entry.type === MiddlewareWatchlistEntryDtoTypeEnum.Option),
            (entry) => entry.symbol,
        );
        const uniqueBondEntries = uniqBy(
            entries.filter((entry) => entry.type === MiddlewareWatchlistEntryDtoTypeEnum.Bond),
            (entry) => entry.symbol,
        );
        const equityEntries = uniqueEquityEntries.map((entry) => entry.symbol);
        const cryptoEntries = uniqueCryptoEntries.map((entry) => entry.symbol);
        const optionEntries = uniqueOptionEntries.map((entry) => entry.symbol);
        const bondEntries = uniqueBondEntries.map((entry) => entry.symbol);
        const equityQuotes = equityEntries.length > 0 ? await this.quoteApi.getQuotes(equityEntries) : [];
        const cryptoQuotes = cryptoEntries.length > 0 ? await this.quoteApi.getQuotes(cryptoEntries, true) : [];
        const optionQuotes =
            optionEntries.length > 0
                ? await Promise.all(
                      optionEntries.map((optionEntry) =>
                          this.quoteApi
                              .getOptionQuotes([optionEntry])
                              .then((quotes) => quotes.find((quote) => quote.symbol === optionEntry) || null),
                      ),
                  )
                : [];
        const bondQuotes =
            bondEntries.length > 0
                ? await Promise.all(bondEntries.map((bondEntry) => this.quoteApi.getBondQuote(bondEntry)))
                : [];
        const filteredOptionQuotes = optionQuotes.filter((optionQuote) => optionQuote !== null);
        const filteredBondQuotes = bondQuotes.filter((bondQuote) => bondQuote !== null) as Quote[];
        return [...equityQuotes, ...cryptoQuotes, ...filteredOptionQuotes, ...filteredBondQuotes];
    }

    async getAllWatchlists(tipranks?: boolean): Promise<WatchlistWithQuotes[]> {
        const { data: watchlists } = await this.apis.middleware.watchlist.getAllWatchlists();
        const watchListEntries = watchlists.flatMap((watchlist) => watchlist.entries);
        const quotes = await this.getQuotes(watchListEntries);
        let tipranksData: TiprankOverviewWithExtraInfo[] = [];

        if (tipranks && watchListEntries.length > 0) {
            try {
                tipranksData = await this.tipranksApi.getTipranksAssetsOverview(
                    watchListEntries.map((entry) => entry.symbol),
                );
            } catch {
                tipranksData = [];
            }
        }

        return sortBy(
            watchlists.map((watchlist) => {
                return {
                    ...watchlist,
                    entries: watchlist.entries.map((entry) => {
                        const currentQuote = quotes.find((quote) => quote.symbol === entry.symbol) as Quote;
                        return {
                            ...entry,
                            quote: currentQuote,
                            tipranks: tipranksData.find((tipranks) => tipranks.ticker === entry.symbol),
                        };
                    }),
                };
            }),
            (watchlist) => watchlist.order,
        );
    }

    async getWatchlist(watchlistId: number): Promise<WatchlistWithQuotes> {
        const { data: watchlist } = await this.apis.middleware.watchlist.getWatchlist(watchlistId);

        const quotes = watchlist.entries.length ? await this.getQuotes(watchlist.entries) : [];

        return {
            ...watchlist,
            entries: watchlist.entries.map((entry) => {
                const currentQuote = quotes.find((quote) => quote.symbol === entry.symbol) as Quote;
                return {
                    ...entry,
                    quote: currentQuote,
                };
            }),
        };
    }

    async createWatchlist(
        watchlist: Omit<Optional<MiddlewarePersistWatchlistDto, 'entries'>, 'id'>,
    ): Promise<WatchlistWithQuotes> {
        // @ts-expect-error: // FIXME type issue. Need client update
        const { data: newWatchlist } = await this.apis.middleware.watchlist.createWatchlist(watchlist);
        return this.getWatchlist(newWatchlist.id);
    }

    async updateWatchlist(
        watchlistId: number,
        watchlist: Partial<MiddlewarePersistWatchlistDto>,
    ): Promise<WatchlistWithQuotes> {
        // @ts-expect-error: // FIXME type issue. Need client update
        await this.apis.middleware.watchlist.updateWatchlist(watchlistId, watchlist);
        return this.getWatchlist(watchlistId);
    }

    async deleteWatchlist(watchlistId: number): Promise<void> {
        await this.apis.middleware.watchlist.deleteWatchlist(watchlistId);
    }

    async addEntries(watchlistId: number, entries: MiddlewareWatchlistEntryDto[]) {
        await this.apis.middleware.watchlist.addWatchlistEntries(watchlistId, { entries: this.formatEntries(entries) });
    }

    async updateEntries(watchlistId: number, entries: MiddlewareWatchlistEntryDto[]) {
        await this.apis.middleware.watchlist.updateWatchlistEntries(watchlistId, {
            entries: this.formatEntries(entries),
        });
    }

    async removeEntries(watchlistId: number, symbols: string[]) {
        await this.apis.middleware.watchlist.removeWatchlistEntries(watchlistId, {
            entries: symbols.map((item) => ({ symbol: item })),
        });
    }

    async updateWatchlistOrder(orders: { order: number; watchlistId: number }[]) {
        await this.apis.middleware.watchlist.updateWatchlistOrders({ orders });
    }

    async importWatchmenWatchlists() {
        await this.apis.middleware.watchlist.importWatchmenWatchlists();
    }
}
